import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createRoot, type Root } from "react-dom/client";
import { useAuth } from "../contexts/AuthContext";
import { useActivityFeedData } from "../contexts/ActivityContext";
import {
  ArrowLeftIcon,
  UsersIcon,
  XCircleIcon,
  CrosshairIcon,
  XIcon,
  SlidersIcon,
  PlusIcon,
  MinusIcon,
  ListIcon,
} from "../components/icons/FeatherIcons";
import { type User, type ActivityRequest, ActivityCategory } from "../types";
import { motion, AnimatePresence } from "framer-motion";
import ActivityDetailModal from "../components/ActivityDetailModal";
import ActivityListItem from "../components/ActivityListItem";
import LocationPermissionPrompt from "../components/LocationPermissionPrompt";
import { FILTER_CATEGORIES } from "../constants";
import { loadGoogleMapsApi } from "../services/locationService";
import { toJsDate } from "../utils/toJsDate";
import { Geolocation, PermissionStatus } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
import { usePageTracking } from "@/hooks/usePageTracking";
import { crashlytics, db } from "../services/firebase";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { fallbackImage } from "../utils/getCachedImage";
import { useCachedImage } from "../hooks/useCachedImage";

// Add this interface to replace google.maps.LatLngLiteral
interface LatLngLiteral {
  lat: number;
  lng: number;
}

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

// Component to display cached activity image in list
const ActivityImagePreview: React.FC<{ activity: ActivityRequest }> = ({
  activity,
}) => {
  const cachedImage = useCachedImage(
    activity.imageUrl || "/images/categories/general.jpg",
    activity.title,
    activity.id,
  );
  return (
    <img
      src={cachedImage}
      alt={activity.title}
      className="w-20 h-20 object-cover rounded-lg"
    />
  );
};

// Component to display cached selected item image
const SelectedItemImage: React.FC<{ activity: ActivityRequest }> = ({
  activity,
}) => {
  const cachedImage = useCachedImage(
    activity.imageUrl,
    activity.title,
    activity.id,
  );
  return (
    <img
      onError={handleImageError}
      src={cachedImage}
      alt={activity.title}
      className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
    />
  );
};

interface DiscoveredActivity {
  activity: ActivityRequest;
  position: LatLngLiteral;
}

const categoryStyles: {
  [key: string]: { icon: React.FC<any>; color: string };
} = {};
FILTER_CATEGORIES.forEach((cat) => {
  categoryStyles[cat.value] = {
    icon: cat.Icon,
    color: "var(--primary)",
  };
});

const ActivityPinContent: React.FC<{ activity: ActivityRequest }> = ({
  activity,
}) => {
  const categoryInfo = categoryStyles[activity.category];
  return (
    <div className="flex flex-col items-center cursor-pointer group">
      <div className="w-12 h-12 rounded-2xl border-2 border-white shadow-md transition-transform transform group-hover:scale-110 flex items-center justify-center bg-primary">
        {categoryInfo && <categoryInfo.icon className="w-6 h-6 text-white" />}
      </div>
      <span className="mt-1 text-white text-xs font-semibold bg-black/40 px-2 py-0.5 rounded-full backdrop-blur-sm truncate max-w-[80px]">
        {activity.title}
      </span>
    </div>
  );
};

const UserLocationPin: React.FC = () => (
  <div className="relative flex items-center justify-center w-6 h-6">
    <div className="absolute w-full h-full bg-blue-500 rounded-full animate-ping opacity-75"></div>
    <div className="relative w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-md"></div>
  </div>
);

const MapPage: React.FC = () => {
  usePageTracking("Map", "MapPage");

  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isLoading: authLoading } = useAuth();
  const activityContext = useActivityFeedData();
  const activities = activityContext.activities || [];
  const passedActivityIds = activityContext.passedActivityIds || [];
  // Paging logic will be handled locally
  const loadMoreActivities = undefined;
  const hasMoreActivities = false;
  const isLoadingMore = false;

  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any | null>(null);
  const [markers, setMarkers] = useState<
    { marker: any; unmount: () => void }[]
  >([]);

  const [userLocation, setUserLocation] = useState<LatLngLiteral | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [discoveredActivities, setDiscoveredActivities] = useState<
    DiscoveredActivity[]
  >([]);
  const [selectedItem, setSelectedItem] = useState<ActivityRequest | null>(
    null,
  );
  const ignoreSelectionSyncRef = useRef(false);
  const locationWatchIdRef = useRef<string | null>(null);
  const hasUserLocationRef = useRef(false);
  const lastUserLocationRef = useRef<LatLngLiteral | null>(null);
  const isUserInteractingRef = useRef(false);
  const interactionTimeoutRef = useRef<number | null>(null);
  const userMarkerRef = useRef<any | null>(null);
  const userMarkerRootRef = useRef<Root | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const [showPermissionDeniedPrompt, setShowPermissionDeniedPrompt] =
    useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);

  // Filter State
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    time: "any",
    categories: new Set<string>(["all"]),
    groupSize: 0,
  });
  const [tempFilters, setTempFilters] = useState(filters);

  // List View State
  const [showListView, setShowListView] = useState(true);

  // Local paging for activity list
  const PAGE_SIZE = 10;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Map State Tracking for deselection
  const [previousMapState, setPreviousMapState] = useState<{
    center: LatLngLiteral;
    zoom: number;
  } | null>(null);

  const SG_CENTER = useMemo(() => ({ lat: 1.3521, lng: 103.8198 }), []);

  const getDistanceMeters = useCallback(
    (a: LatLngLiteral, b: LatLngLiteral) => {
      const toRad = (value: number) => (value * Math.PI) / 180;
      const R = 6371000;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const lat1 = toRad(a.lat);
      const lat2 = toRad(b.lat);
      const sinDLat = Math.sin(dLat / 2);
      const sinDLng = Math.sin(dLng / 2);
      const h =
        sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
      return 2 * R * Math.asin(Math.sqrt(h));
    },
    [],
  );

  const canViewPrivateActivity = useCallback(
    (activity: ActivityRequest): boolean => {
      if (!activity.isPrivate) return true;
      if (!currentUser) return false;

      if (activity.creatorId === currentUser.id) return true;
      if (
        activity.participantIds &&
        activity.participantIds.includes(currentUser.id)
      )
        return true;
      if (
        activity.invitedUserIds &&
        activity.invitedUserIds.includes(currentUser.id)
      )
        return true;
      if (
        activity.pendingInvites &&
        activity.pendingInvites.includes(currentUser.id)
      )
        return true;

      return false;
    },
    [currentUser],
  );

  // Filter logic
  const filteredActivities = useMemo(() => {
    // Match home feed: exclude deleted, expired, full, and already joined activities
    return activities.filter((activity) => {
      if (activity.isDeleted) return false;
      if (activity.isReported) return false;
      if (!canViewPrivateActivity(activity)) return false;

      const now = new Date().getTime();
      const dateObj = toJsDate(activity.dateTime);
      if (!dateObj || isNaN(dateObj.getTime())) return false;
      const endTime = dateObj.getTime() + activity.duration * 60 * 1000;
      if (endTime < now) return false;

      // Exclude full activities
      if (activity.participants.length >= activity.groupSize) return false;

      // Exclude activities already joined by current user
      if (
        currentUser &&
        activity.participants.some((p) => p.id === currentUser.id)
      )
        return false;
      if (currentUser && activity.participantIds?.includes(currentUser.id))
        return false;

      // Exclude activities created by the current user
      if (currentUser && activity.creator.id === currentUser.id) return false;

      // Exclude activities the user has passed
      if (
        typeof passedActivityIds !== "undefined" &&
        passedActivityIds.includes(activity.id)
      )
        return false;

      // Time filter
      if (filters.time !== "any") {
        const activityDate = toJsDate(activity.dateTime);
        if (filters.time === "now") {
          const oneHourFromNow = new Date(now + 60 * 60 * 1000);
          if (
            !(activityDate >= new Date(now) && activityDate <= oneHourFromNow)
          )
            return false;
        } else if (filters.time === "today") {
          const today = new Date();
          if (
            activityDate.getFullYear() !== today.getFullYear() ||
            activityDate.getMonth() !== today.getMonth() ||
            activityDate.getDate() !== today.getDate()
          )
            return false;
        } else if (filters.time === "tomorrow") {
          const tomorrow = new Date();
          tomorrow.setDate(new Date().getDate() + 1);
          if (
            activityDate.getFullYear() !== tomorrow.getFullYear() ||
            activityDate.getMonth() !== tomorrow.getMonth() ||
            activityDate.getDate() !== tomorrow.getDate()
          )
            return false;
        }
      }

      // Category filter - check if activity's category is in selected categories
      if (
        !filters.categories.has("all") &&
        !filters.categories.has(activity.category)
      ) {
        return false;
      }

      // Group size filter
      if (
        filters.groupSize > 0 &&
        activity.participants.length < filters.groupSize
      ) {
        return false;
      }

      return true;
    });
  }, [activities, filters, canViewPrivateActivity]);

  const sortedFilteredActivities = useMemo(() => {
    return [...filteredActivities].sort((a, b) => {
      if (!a || !a.dateTime) return 1;
      if (!b || !b.dateTime) return -1;
      const aTime = new Date(a.dateTime).getTime();
      const bTime = new Date(b.dateTime).getTime();
      return aTime - bTime;
    });
  }, [filteredActivities]);

  // Reset visibleCount when filters or list view changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filters, showListView, sortedFilteredActivities.length]);

  const tempFilteredCount = useMemo(() => {
    return activities.filter((activity) => {
      // Exclude deleted activities
      if (activity.isDeleted) return false;
      if (activity.isReported) return false;
      if (!canViewPrivateActivity(activity)) return false;

      // Exclude expired activities (same logic as main filter)
      const now = new Date().getTime();
      const dateObj = toJsDate(activity.dateTime);
      if (!dateObj || isNaN(dateObj.getTime())) return false;
      const endTime = dateObj.getTime() + activity.duration * 60 * 1000;
      if (endTime < now) return false;

      // Time filter (same logic as main filter)
      if (tempFilters.time !== "any") {
        const activityDate = toJsDate(activity.dateTime);

        if (tempFilters.time === "now") {
          const oneHourFromNow = new Date(now + 60 * 60 * 1000);
          if (
            !(activityDate >= new Date(now) && activityDate <= oneHourFromNow)
          )
            return false;
        } else if (tempFilters.time === "today") {
          const today = new Date();
          if (
            activityDate.getFullYear() !== today.getFullYear() ||
            activityDate.getMonth() !== today.getMonth() ||
            activityDate.getDate() !== today.getDate()
          )
            return false;
        } else if (tempFilters.time === "tomorrow") {
          const tomorrow = new Date();
          tomorrow.setDate(new Date().getDate() + 1);
          if (
            activityDate.getFullYear() !== tomorrow.getFullYear() ||
            activityDate.getMonth() !== tomorrow.getMonth() ||
            activityDate.getDate() !== tomorrow.getDate()
          )
            return false;
        }
      }

      // Category filter - check if activity's category is in selected categories
      if (
        !tempFilters.categories.has("all") &&
        !tempFilters.categories.has(activity.category)
      ) {
        return false;
      }

      // Group size filter
      if (
        tempFilters.groupSize > 0 &&
        activity.participants.length < tempFilters.groupSize
      ) {
        return false;
      }

      return true;
    }).length;
  }, [activities, tempFilters, canViewPrivateActivity]);

  // Compute filtered count for filter panel button
  const filterPanelCount = useMemo(() => {
    const now = new Date().getTime();
    return activities.filter((activity) => {
      if (activity.isDeleted) return false;
      if (activity.isReported) return false;
      if (!canViewPrivateActivity(activity)) return false;
      const dateObj = toJsDate(activity.dateTime);
      if (!dateObj || isNaN(dateObj.getTime())) return false;
      const endTime = dateObj.getTime() + activity.duration * 60 * 1000;
      if (endTime < now) return false;
      // Exclude full activities
      if (activity.participants.length >= activity.groupSize) return false;
      // Exclude activities already joined by current user
      if (
        currentUser &&
        activity.participants.some((p) => p.id === currentUser.id)
      )
        return false;
      if (currentUser && activity.participantIds?.includes(currentUser.id))
        return false;
      // Exclude activities created by the current user
      if (currentUser && activity.creator.id === currentUser.id) return false;
      // Exclude activities the user has passed
      if (
        typeof passedActivityIds !== "undefined" &&
        passedActivityIds.includes(activity.id)
      )
        return false;
      // Time filter
      if (tempFilters.time !== "any") {
        const activityDate = toJsDate(activity.dateTime);
        if (!activityDate || isNaN(activityDate.getTime())) return false;
        if (tempFilters.time === "now") {
          const oneHourFromNow = new Date(now + 60 * 60 * 1000);
          if (
            !(activityDate >= new Date(now) && activityDate <= oneHourFromNow)
          )
            return false;
        } else if (tempFilters.time === "today") {
          const today = new Date();
          if (
            activityDate.getFullYear() !== today.getFullYear() ||
            activityDate.getMonth() !== today.getMonth() ||
            activityDate.getDate() !== today.getDate()
          )
            return false;
        } else if (tempFilters.time === "tomorrow") {
          const tomorrow = new Date();
          tomorrow.setDate(new Date().getDate() + 1);
          if (
            activityDate.getFullYear() !== tomorrow.getFullYear() ||
            activityDate.getMonth() !== tomorrow.getMonth() ||
            activityDate.getDate() !== tomorrow.getDate()
          )
            return false;
        }
      }
      // Category filter - check if activity's category is in selected categories
      if (
        !tempFilters.categories.has("all") &&
        !tempFilters.categories.has(activity.category)
      ) {
        return false;
      }
      // Group size filter
      if (
        tempFilters.groupSize > 0 &&
        activity.participants.length < tempFilters.groupSize
      ) {
        return false;
      }
      return true;
    }).length;
  }, [
    activities,
    tempFilters,
    currentUser,
    passedActivityIds,
    canViewPrivateActivity,
  ]);

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setIsFilterPanelOpen(false);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      time: "any",
      categories: new Set<string>(["all"]),
      groupSize: 0,
    };
    setTempFilters(defaultFilters);
    setFilters(defaultFilters);
    handleDeselectItem(); // Deselect any selected activity
    setIsFilterPanelOpen(false);
  };

  const updateSelectionState = useCallback(
    (activityId: string | null) => {
      const nextState = {
        ...((location.state as Record<string, unknown>) || {}),
      };
      if (activityId) {
        nextState.selectedActivityId = activityId;
      } else {
        delete nextState.selectedActivityId;
      }
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: nextState,
      });
    },
    [navigate, location.pathname, location.search, location.state],
  );

  const selectedActivityIdFromState = (
    location.state as { selectedActivityId?: string } | null
  )?.selectedActivityId;

  useEffect(() => {
    if (ignoreSelectionSyncRef.current) {
      ignoreSelectionSyncRef.current = false;
      return;
    }

    if (isUserInteractingRef.current) {
      return;
    }

    if (!selectedActivityIdFromState) return;
    const activity = activities.find(
      (a) => a.id === selectedActivityIdFromState,
    );
    if (!activity) return;

    if (selectedItem?.id !== selectedActivityIdFromState) {
      setSelectedItem(activity);
    }

    if (map) {
      const position =
        discoveredActivities.find((da) => da.activity.id === activity.id)
          ?.position || activity.coordinates;
      if (position) {
        const center = map.getCenter?.();
        const isSameCenter =
          center &&
          Math.abs(center.lat() - position.lat) < 0.000001 &&
          Math.abs(center.lng() - position.lng) < 0.000001;
        const currentZoom = map.getZoom();
        const targetZoom = Math.max(currentZoom, 15);

        if (!isSameCenter) {
          map.panTo(position);
        }

        if (currentZoom < targetZoom) {
          map.setZoom(targetZoom);
        }
      }
    }
  }, [
    selectedActivityIdFromState,
    selectedItem?.id,
    activities,
    map,
    discoveredActivities,
  ]);

  // Load Google Maps API script
  useEffect(() => {
    loadGoogleMapsApi(["places", "marker"])
      .then(() => {
        // Add additional check to ensure google.maps is actually available
        if ((window as any).google?.maps?.Map) {
          // Check if marker library is available
          const hasAdvancedMarker = !!(window as any).google?.maps?.marker
            ?.AdvancedMarkerElement;
          const hasRegularMarker = !!(window as any).google?.maps?.Marker;

          setIsApiLoaded(true);
          setMapError(null);
        } else {
          // Wait a bit more and check again
          setTimeout(() => {
            if ((window as any).google?.maps?.Map) {
              setIsApiLoaded(true);
              setMapError(null);
            } else {
              setMapError("Google Maps API failed to load properly.");
            }
          }, 1500);
        }
      })
      .catch((error: Error) => {
        crashlytics.logError(
          `Error loading Google Maps API: ${JSON.stringify(error?.message)}`,
          "MapPage.tsx",
          error,
        );
        setMapError(error.message);
      });
  }, []);

  const checkAndRequestPermissions = async () => {
    if (Capacitor.isNativePlatform()) {
      let status: PermissionStatus = await Geolocation.checkPermissions();
      if (status.location === "granted") {
        // Proceed to get location
        return true;
      } else if (status.location === "denied") {
        // The user likely permanently denied it.
        // You should show a custom UI/dialog explaining why location is needed
        // and provide a button to direct them to the app's native settings.
        // A dedicated plugin, like the 'cordova-diagnostic-plugin', has methods
        // to open the app's settings directly if you choose to install it.
        return false;
      } else if (
        status.location === "prompt" ||
        status.location === "prompt-with-rationale"
      ) {
        // Permission not yet granted or denied, request it
        const requestResult = await Geolocation.requestPermissions({
          permissions: ["location", "coarseLocation"],
        });
        if (requestResult.location === "granted") {
          return true;
        } else {
          return false;
        }
      }
    }
    return true;
  };

  const requestLocation = useCallback(
    async (options?: { highAccuracy?: boolean; shouldCenter?: boolean }) => {
      if (!isApiLoaded) {
        setLocationError("Map is not yet loaded.");
        return;
      }

      const highAccuracy = options?.highAccuracy ?? false;
      const shouldCenter = options?.shouldCenter ?? true;

      setIsRequestingLocation(true);
      setLocationError(null);
      setShowPermissionDeniedPrompt(false); // Hide prompt when retrying

      const granted = await checkAndRequestPermissions();
      if (!granted) {
        setIsRequestingLocation(false);
        setShowPermissionDeniedPrompt(true);
        return;
      }

      const isTimeoutError = (error: any) => {
        const errorMessage = String(error?.message || "").toLowerCase();
        const errorCode = String(error?.code || "");
        return (
          errorMessage.includes("timed out") ||
          errorCode === "OS-PLUG-GLOC-0010"
        );
      };

      try {
        // Use Capacitor's Geolocation instead of navigator.geolocation
        // Increase timeout for web/emulator environments
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: highAccuracy,
          timeout: 30000, // Increased to 30s for web browsers
          maximumAge: 0,
        });

        const { latitude, longitude } = position.coords;
        const newUserLocation = { lat: latitude, lng: longitude };
        setUserLocation(newUserLocation);
        lastUserLocationRef.current = newUserLocation;

        if (db && currentUser) {
          try {
            await updateDoc(doc(db, "users", currentUser.id), {
              lastKnownCoordinates: newUserLocation,
              lastKnownCoordinatesUpdatedAt: serverTimestamp(),
            });
          } catch (error) {
            crashlytics.logError(
              `Error updating lastKnownCoordinates: ${JSON.stringify(error?.message)}`,
              "MapPage.tsx",
              error,
            );
          }
        }

        if (map && shouldCenter) {
          map.setCenter(newUserLocation);
          map.setZoom(15);
        }

        setIsRequestingLocation(false);
        setShowPermissionDeniedPrompt(false); // Ensure prompt is hidden on success
      } catch (error: any) {
        if (isTimeoutError(error)) {
          try {
            // Try again with lower accuracy and longer timeout
            const fallbackPosition = await Geolocation.getCurrentPosition({
              enableHighAccuracy: false,
              timeout: 45000, // Increased to 45s for web browsers
              maximumAge: 60000, // Accept cached position up to 60s old
            });

            const { latitude, longitude } = fallbackPosition.coords;
            const newUserLocation = { lat: latitude, lng: longitude };
            setUserLocation(newUserLocation);
            lastUserLocationRef.current = newUserLocation;

            if (db && currentUser) {
              try {
                await updateDoc(doc(db, "users", currentUser.id), {
                  lastKnownCoordinates: newUserLocation,
                  lastKnownCoordinatesUpdatedAt: serverTimestamp(),
                });
              } catch (updateError) {
                crashlytics.logError(
                  `Error updating lastKnownCoordinates: ${JSON.stringify(updateError?.message)}`,
                  "MapPage.tsx",
                  updateError,
                );
              }
            }

            if (map && shouldCenter) {
              map.setCenter(newUserLocation);
              map.setZoom(15);
            }

            setIsRequestingLocation(false);
            setShowPermissionDeniedPrompt(false);
            return;
          } catch (fallbackError) {
            error = fallbackError;
          }
        }

        crashlytics.logError(
          `Error getting current position: ${JSON.stringify(error?.message)}`,
          "MapPage.tsx",
          error,
        );

        // Check if it's a permission denied error
        if (
          error.message?.includes("permission") ||
          error.message?.includes("denied")
        ) {
          setShowPermissionDeniedPrompt(true); // Show the guide prompt on denial
        } else {
          const isTimeout = isTimeoutError(error);
          if (isTimeout) {
            // For web/development, use default Singapore location on timeout
            if (!Capacitor.isNativePlatform() && window.location.hostname === 'localhost') {
              const defaultLocation = SG_CENTER;
              setUserLocation(defaultLocation);
              lastUserLocationRef.current = defaultLocation;

              if (map && shouldCenter) {
                map.setCenter(defaultLocation);
                map.setZoom(12);
              }

              setLocationError(
                "Using default location (Singapore). Enable browser location access for accurate positioning.",
              );
            } else {
              setLocationError(
                "Location request timed out. Move to an open area and try again.",
              );
            }
          } else {
            setLocationError(
              "Could not get your location. Please check your connection or device settings.",
            );
          }
        }

        setIsRequestingLocation(false);
      }
    },
    [isApiLoaded, map],
  );

  // Automatically request location once the map is ready.
  useEffect(() => {
    if (isApiLoaded && map && !userLocation) {
      requestLocation({ highAccuracy: false, shouldCenter: true });
    }
  }, [isApiLoaded, map, userLocation, requestLocation]);

  useEffect(() => {
    hasUserLocationRef.current = Boolean(userLocation);
  }, [userLocation]);

  useEffect(() => {
    let isActive = true;

    const startLocationWatch = async () => {
      if (!isApiLoaded) return;
      if (locationWatchIdRef.current) return;

      const granted = await checkAndRequestPermissions();
      if (!granted || !isActive) return;

      const watchId = await Geolocation.watchPosition(
        {
          enableHighAccuracy: false,
          timeout: 20000,
          maximumAge: 30000,
          minimumUpdateInterval: 5000,
        },
        (position, error) => {
          if (!isActive) return;

          if (position?.coords) {
            const accuracy = position.coords.accuracy ?? 0;
            const newUserLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            const lastLocation = lastUserLocationRef.current;
            const hasLocation = Boolean(lastLocation);
            const isLowQuality = accuracy > 100;
            const movedEnough =
              !lastLocation ||
              getDistanceMeters(lastLocation, newUserLocation) > 25;

            if (!hasLocation || (!isLowQuality && movedEnough)) {
              setUserLocation(newUserLocation);
              lastUserLocationRef.current = newUserLocation;
              setLocationError(null);
            }
          } else if (error && !hasUserLocationRef.current) {
            setLocationError(
              "Could not get your location. Please check your connection or device settings.",
            );
          }
        },
      );

      locationWatchIdRef.current = watchId;
    };

    startLocationWatch();

    return () => {
      isActive = false;
      if (locationWatchIdRef.current) {
        Geolocation.clearWatch({ id: locationWatchIdRef.current });
        locationWatchIdRef.current = null;
      }
    };
  }, [isApiLoaded, getDistanceMeters]);

  // Initialize Map
  useEffect(() => {
    if (!isApiLoaded || !mapRef.current || map || mapError) return;

    // Double-check that google.maps.Map is available
    if (!(window as any).google?.maps?.Map) {
      crashlytics.logError(
        "Google Maps API not fully loaded",
        "MapPage.tsx",
        new Error("Google Maps API not fully loaded"),
      );
      setMapError("Google Maps API not fully loaded");
      return;
    }

    try {
      const mapInstance = new (window as any).google.maps.Map(mapRef.current, {
        center: SG_CENTER,
        zoom: 12,
        mapId: "ACTIVITY_LINK_MAP_STYLE",
        disableDefaultUI: true,
        // Note: styles removed because they conflict with mapId
        // Configure styles in Google Cloud Console for the mapId instead
      });
      setMap(mapInstance);
      // Auto-fit bounds to relevant activities after map is created
      setTimeout(() => {
        if (filteredActivities.length > 0) {
          const bounds = new (window as any).google.maps.LatLngBounds();
          filteredActivities.forEach((activity) => {
            if (activity.coordinates) {
              bounds.extend(activity.coordinates);
            }
          });
          if (!bounds.isEmpty()) {
            mapInstance.fitBounds(bounds);
          }
        }
      }, 500); // Delay to ensure map is rendered
    } catch (error) {
      crashlytics.logError(
        `Error initializing map: ${JSON.stringify(error?.message)}`,
        "MapPage.tsx",
        error,
      );
      setMapError("Failed to initialize map. Please refresh the page.");
    }
  }, [isApiLoaded, map, SG_CENTER, mapError]);

  useEffect(() => {
    if (!map) return;

    const markInteracting = () => {
      isUserInteractingRef.current = true;
      if (interactionTimeoutRef.current) {
        window.clearTimeout(interactionTimeoutRef.current);
      }
    };

    const clearInteracting = () => {
      if (interactionTimeoutRef.current) {
        window.clearTimeout(interactionTimeoutRef.current);
      }
      interactionTimeoutRef.current = window.setTimeout(() => {
        isUserInteractingRef.current = false;
      }, 500);
    };

    const dragStartListener = map.addListener("dragstart", markInteracting);
    const zoomChangedListener = map.addListener(
      "zoom_changed",
      markInteracting,
    );
    const idleListener = map.addListener("idle", clearInteracting);

    return () => {
      dragStartListener.remove();
      zoomChangedListener.remove();
      idleListener.remove();
      if (interactionTimeoutRef.current) {
        window.clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, [map]);

  // Fetch and position items based on filters
  useEffect(() => {
    if (authLoading || !currentUser) return;
    const positionFilteredActivities = () => {
      const centerPoint = SG_CENTER;

      const positioned = filteredActivities
        .map((activity) => {
          // Use real coordinates if they exist
          if (activity.coordinates) {
            return { activity, position: activity.coordinates };
          }
          // Fallback for older data without coordinates: generate random coordinates around center
          return {
            activity,
            position: {
              lat: centerPoint.lat + (Math.random() - 0.5) * 0.05,
              lng: centerPoint.lng + (Math.random() - 0.5) * 0.05,
            },
          };
        })
        .filter((item): item is DiscoveredActivity => !!item.position);

      // Auto-fit bounds when filtered activities change
      if (
        map &&
        filteredActivities.length > 0 &&
        !isUserInteractingRef.current &&
        !selectedItem
      ) {
        const bounds = new (window as any).google.maps.LatLngBounds();
        let hasValidCoordinates = false;

        filteredActivities.forEach((activity) => {
          if (activity.coordinates) {
            bounds.extend(activity.coordinates);
            hasValidCoordinates = true;
          }
        });

        if (hasValidCoordinates && !bounds.isEmpty()) {
          map.fitBounds(bounds);
        }
      }

      setDiscoveredActivities(positioned);
    };

    positionFilteredActivities();
  }, [
    authLoading,
    currentUser,
    filteredActivities,
    SG_CENTER,
    map,
    selectedItem,
  ]);

  // Update markers on map
  useEffect(() => {
    if (!map) return;

    markers.forEach(({ marker, unmount }) => {
      if (marker) {
        // Properly remove marker from map
        if (marker.setMap) {
          marker.setMap(null);
        } else if (marker.map !== undefined) {
          marker.map = null;
        }
      }
      // Use requestAnimationFrame for better performance than setTimeout
      requestAnimationFrame(() => {
        try {
          unmount();
        } catch (error) {
          console.warn("Error unmounting marker:", error);
        }
      });
    });

    const newMarkers: { marker: any; unmount: () => void }[] = [];

    const createMarker = (item: DiscoveredActivity) => {
      const container = document.createElement("div");
      container.style.cursor = "pointer";
      const root = createRoot(container);

      const position = item.position;

      // Use requestAnimationFrame to avoid forced reflow
      requestAnimationFrame(() => {
        root.render(<ActivityPinContent activity={item.activity} />);
      });
      // Check if AdvancedMarkerElement is available, otherwise fallback to regular Marker
      let marker;
      try {
        if ((window as any).google?.maps?.marker?.AdvancedMarkerElement) {
          marker = new (window as any).google.maps.marker.AdvancedMarkerElement(
            {
              map,
              position,
              content: container,
            },
          );
          marker.addListener("gmp-click", () => handleSelectItem(item.activity));
        } else {
          // Create category-specific custom markers with SVG icons
          const getCategoryMarker = (category: string) => {
            switch (category) {
              case "movies":
                return {
                  color: "#EF4444",
                  name: "Movies",
                  icon: `<path d="M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 2v14h14V5H5zm2 2h2v2H7V7zm4 0h2v2h-2V7zm4 0h2v2h-2V7zM7 11h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2zM7 15h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z" fill="white"/>`,
                };
              case "dining":
                return {
                  color: "#F97316",
                  name: "Dining",
                  icon: `<path d="M8.5 8.5c0 .28-.22.5-.5.5s-.5-.22-.5-.5V3c0-.28.22-.5.5-.5s.5.22.5.5v5.5zm0 0V21c0 .28-.22.5-.5.5s-.5-.22-.5-.5V8.5zm7.5-5.5c0-.28.22-.5.5-.5s.5.22.5.5v18c0 .28-.22.5-.5.5s-.5-.22-.5-.5V3zm-2 5.5c0 .28-.22.5-.5.5s-.5-.22-.5-.5V3c0-.28.22-.5.5-.5s.5.22.5.5v5.5zm0 0V21c0 .28-.22.5-.5.5s-.5-.22-.5-.5V8.5zm-2-5.5c0-.28.22-.5.5-.5s.5.22.5.5v5.5c0 .28-.22.5-.5.5s-.5-.22-.5-.5V3z" fill="white"/>`,
                };
              case "games":
                return {
                  color: "#8B5CF6",
                  name: "Games",
                  icon: `<path d="M6 4h12c3.31 0 6 2.69 6 6 0 2.76-1.88 5.1-4.42 5.79L18 21h-2l-1.73-5.79C13.51 15.69 12.78 16 12 16s-1.51-.31-2.27-.79L8 21H6l-1.58-5.21C1.88 15.1 0 12.76 0 10c0-3.31 2.69-6 6-6zm0 2C3.79 6 2 7.79 2 10s1.79 4 4 4h1c.55 0 1-.45 1-1s-.45-1-1-1H6c-1.1 0-2-.9-2-2s.9-2 2-2h1c.55 0 1-.45 1-1s-.45-1-1-1H6zm12 0h-1c-.55 0-1 .45-1 1s.45 1 1 1h1c1.1 0 2 .9 2 2s-.9 2-2 2h-1c-.55 0-1 .45-1 1s.45 1 1 1h1c2.21 0 4-1.79 4-4s-1.79-4-4-4z" fill="white"/>`,
                };
              case "outdoor":
                return {
                  color: "#10B981",
                  name: "Outdoor",
                  icon: `<circle cx="12" cy="8" r="3" fill="white"/><path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74L12 2z" fill="white"/>`,
                };
              case "drinks":
                return {
                  color: "#F59E0B",
                  name: "Drinks",
                  icon: `<path d="M4 19h16v2H4v-2zM20 3H4v10c0 2.21 1.79 4 4 4h8c2.21 0 4-1.79 4-4V3zm-2 10c0 1.1-.9 2-2 2H8c-1.1 0-2-.9-2-2V5h12v8zM7 7v2h2V7H7zm4 0v2h2V7h-2zm4 0v2h2V7h-2z" fill="white"/>`,
                };
              case "fitness":
                return {
                  color: "#06B6D4",
                  name: "Fitness",
                  icon: `<path d="M20.57 14.86L22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29l-1.43-1.43z" fill="white"/>`,
                };
              case "general":
                return {
                  color: "#9fa4b0ff",
                  name: "General",
                  icon: `<path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 7H16c-.8 0-1.54.37-2.01.99L12 10.5l-2-2.5C9.54 7.37 8.8 7 8 7H5.46c-.8 0-1.3.63-1.42 1.37L1.5 16H4v6h2v-6h.5L8 14l1.5 2H10v6h2v-6h2v6h2zm-8-12.5L10.5 8 8 10.5 6.5 8 8 9.5z" fill="white"/>`,
                };
              default:
                return {
                  color: "#6366f1",
                  name: "Activity",
                  icon: `<circle cx="12" cy="12" r="3" fill="white"/>`,
                };
            }
          };

          const categoryMarker = getCategoryMarker(item.activity.category);

          marker = new (window as any).google.maps.Marker({
            position,
            map,
            title: `${categoryMarker.name}: ${item.activity.title}`,
            optimized: false,
            icon: {
              url:
                "data:image/svg+xml;charset=UTF-8," +
                encodeURIComponent(`
                                <svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
                                    <defs>
                                        <filter id="shadow${item.activity.category}" x="-50%" y="-50%" width="200%" height="200%">
                                            <feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="rgba(0,0,0,0.5)"/>
                                        </filter>
                                    </defs>
                                    <g filter="url(#shadow${item.activity.category})">
                                        <!-- Location pin shape -->
                                        <path d="M18 2C11.383 2 6 7.383 6 14c0 9 12 28 12 28s12-19 12-28c0-6.617-5.383-12-12-12z"
                                              fill="${categoryMarker.color}" stroke="white" stroke-width="2"/>
                                        <!-- Icon inside pin -->
                                        <g transform="translate(9, 8)" fill="white">
                                            <svg width="18" height="18" viewBox="0 0 24 24">
                                                ${categoryMarker.icon}
                                            </svg>
                                        </g>
                                    </g>
                                </svg>
                            `),
              scaledSize: new (window as any).google.maps.Size(36, 48),
              anchor: new (window as any).google.maps.Point(18, 44),
            },
          });

          // Add click listener for regular marker
          marker.addListener("click", () => handleSelectItem(item.activity));
        }
      } catch (error) {
        crashlytics.logError(
          `Error creating marker: ${JSON.stringify(error?.message)}`,
          "MapPage.tsx",
          error,
        );
        // Final fallback with category-aware styling
        const getCategoryColor = (category: string) => {
          switch (category) {
            case "movies":
              return "#EF4444";
            case "dining":
              return "#F97316";
            case "games":
              return "#8B5CF6";
            case "outdoor":
              return "#10B981";
            case "drinks":
              return "#F59E0B";
            case "fitness":
              return "#06B6D4";
            case "general":
              return "#6B7280";
            default:
              return "#6366f1";
          }
        };

        marker = new (window as any).google.maps.Marker({
          position,
          map,
          title: item.activity.title,
          optimized: false,
          icon: {
            path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
            scale: 1.5,
            fillColor: getCategoryColor(item.activity.category),
            fillOpacity: 1,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
            anchor: new (window as any).google.maps.Point(12, 22),
          },
        });

        // Add click listener for regular marker
        marker.addListener("click", () => handleSelectItem(item.activity));
      }

      newMarkers.push({ marker, unmount: () => root.unmount() });
    };

    discoveredActivities.forEach((item) => createMarker(item));

    setMarkers(newMarkers);

    return () => {
      newMarkers.forEach(({ marker, unmount }) => {
        if (marker) {
          // Properly remove marker from map
          if (marker.setMap) {
            marker.setMap(null);
          } else if (marker.map !== undefined) {
            marker.map = null;
          }
        }
        // Use requestAnimationFrame for cleanup
        requestAnimationFrame(() => {
          try {
            unmount();
          } catch (error) {
            console.warn("Error cleaning up marker:", error);
          }
        });
      });
    };
  }, [map, discoveredActivities]);

  useEffect(() => {
    return () => {
      if (userMarkerRef.current) {
        if (userMarkerRef.current.setMap) {
          userMarkerRef.current.setMap(null);
        } else if (userMarkerRef.current.map !== undefined) {
          userMarkerRef.current.map = null;
        }
        userMarkerRef.current = null;
      }
      if (userMarkerRootRef.current) {
        userMarkerRootRef.current.unmount();
        userMarkerRootRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !userLocation) return;

    if (!userMarkerRef.current) {
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(<UserLocationPin />);
      userMarkerRootRef.current = root;

      try {
        if ((window as any).google?.maps?.marker?.AdvancedMarkerElement) {
          userMarkerRef.current = new (
            window as any
          ).google.maps.marker.AdvancedMarkerElement({
            map,
            position: userLocation,
            content: container,
            zIndex: 999,
          });
        } else {
          userMarkerRef.current = new (window as any).google.maps.Marker({
            position: userLocation,
            map,
            title: "Your Location",
            zIndex: 999,
            optimized: false,
          });
        }
      } catch (error) {
        crashlytics.logError(
          `Error creating user location marker: ${JSON.stringify(error?.message)}`,
          "MapPage.tsx",
          error,
        );
        userMarkerRef.current = new (window as any).google.maps.Marker({
          position: userLocation,
          map,
          title: "Your Location",
          zIndex: 999,
          optimized: false,
        });
      }
      return;
    }

    if (userMarkerRef.current.setPosition) {
      userMarkerRef.current.setPosition(userLocation);
    } else if (userMarkerRef.current.position !== undefined) {
      userMarkerRef.current.position = userLocation;
    }
  }, [map, userLocation]);

  const handleSelectItem = (item: ActivityRequest) => {
    // If clicking the same item that's already selected, deselect it
    if (selectedItem && selectedItem.id === item.id) {
      handleDeselectItem();
      return;
    }

    ignoreSelectionSyncRef.current = false;

    // Save current map state before selecting (only if no item is currently selected)
    if (map && !selectedItem) {
      const currentCenter = map.getCenter();
      const currentZoom = map.getZoom();
      setPreviousMapState({
        center: { lat: currentCenter.lat(), lng: currentCenter.lng() },
        zoom: currentZoom,
      });
    }

    setSelectedItem(item);
    updateSelectionState(item.id);
    if (map) {
      const position = discoveredActivities.find(
        (da) => da.activity.id === item.id,
      )?.position;
      if (position) {
        map.panTo(position);
        map.setZoom(Math.max(map.getZoom(), 15)); // Ensure a reasonable zoom level
      }
    }
  };

  const handleDeselectItem = () => {
    ignoreSelectionSyncRef.current = true;
    setSelectedItem(null);
    updateSelectionState(null);

    // Reset map to show all activities
    if (map) {
      if (discoveredActivities.length > 0) {
        // Create bounds that include all discovered positions
        const bounds = new (window as any).google.maps.LatLngBounds();

        discoveredActivities.forEach((item) => {
          if (item.position) {
            bounds.extend(item.position);
          }
        });

        if (!bounds.isEmpty()) {
          const padding = 50; // pixels
          map.fitBounds(bounds, padding);
        } else {
          map.setCenter(SG_CENTER);
          map.setZoom(12);
        }
      } else {
        map.setCenter(SG_CENTER);
        map.setZoom(12);
      }
    }
    setPreviousMapState(null);
  };

  const handleListItemClick = (activity: ActivityRequest) => {
    if (selectedItem?.id === activity.id) {
      // Deselect if clicking on already selected item
      handleDeselectItem();
    } else {
      // Select the new item
      handleSelectItem(activity);
    }
  };

  // Scroll selected item into view when selected from map
  useEffect(() => {
    if (selectedItem && showListView) {
      const selectedElement = document.querySelector(
        `[data-activity-id="${selectedItem.id}"]`,
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [selectedItem, showListView]);

  useEffect(() => {
    if (!map) return;

    const resizeMap = () => {
      const maps = (window as any).google?.maps;
      if (!maps?.event) return;

      maps.event.trigger(map, "resize");
      const center = map.getCenter?.();
      if (center) {
        map.setCenter(center);
      }
    };

    const timeoutId = window.setTimeout(resizeMap, 0);
    return () => window.clearTimeout(timeoutId);
  }, [map, showListView, isFilterPanelOpen]);

  const handleCloseSheet = (e?: React.MouseEvent) => {
    if (e && e.target !== e.currentTarget) return;
    handleDeselectItem();
  };

  const handleCenterOnUser = () => {
    if (map && userLocation) {
      setSelectedItem(null);
      setPreviousMapState(null);

      // If already centered on user (zoomed in), deselect and zoom out to show all
      const currentCenter = map.getCenter();
      const currentZoom = map.getZoom();

      // Check if we're already centered on user location (within small threshold)
      const lat = currentCenter.lat();
      const lng = currentCenter.lng();
      const isAlreadyCentered =
        Math.abs(lat - userLocation.lat) < 0.001 &&
        Math.abs(lng - userLocation.lng) < 0.001 &&
        currentZoom >= 15;

      if (isAlreadyCentered) {
        // Deselect and zoom out to show all activities (same logic as handleDeselectItem)
        if (filteredActivities.length > 0) {
          const bounds = new (window as any).google.maps.LatLngBounds();
          let hasValidCoordinates = false;

          filteredActivities.forEach((activity) => {
            if (activity.coordinates) {
              bounds.extend(activity.coordinates);
              hasValidCoordinates = true;
            }
          });

          if (userLocation) {
            bounds.extend(userLocation);
            hasValidCoordinates = true;
          }

          if (hasValidCoordinates && !bounds.isEmpty()) {
            const padding = 50;
            map.fitBounds(bounds, padding);
          } else {
            map.setCenter(SG_CENTER);
            map.setZoom(12);
          }
        } else {
          map.setCenter(SG_CENTER);
          map.setZoom(12);
        }
        return;
      } else {
        // Center on user location
        map.panTo(userLocation);
        map.setZoom(15);
      }
    }

    requestLocation({ highAccuracy: true, shouldCenter: true });
  };

  const handleViewDetails = (activity: ActivityRequest) => {
    setSelectedItem(activity);
    setIsDetailModalOpen(true);
  };

  const handleNavigateToDetails = (activity: ActivityRequest) => {
    updateSelectionState(activity.id);
    navigate(`/activity/${activity.id}`);
  };

  if (authLoading || !currentUser)
    return <div className="bg-gray-800 h-full"></div>;

  const finalError = mapError || locationError;
  const errorTitle = mapError ? "Map Unavailable" : "Location Unavailable";

  return (
    <>
      <ActivityDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        activity={selectedItem}
      />
      <div
        className="relative h-full bg-gray-800 overflow-hidden flex flex-col"
        onClick={handleCloseSheet}
      >
        <div
          ref={mapRef}
          className={`w-full transition-all duration-300 ease-in-out ${
            showListView ? "flex-1" : "h-full"
          }`}
        />

        {/* Activities List */}
        <AnimatePresence>
          {showListView && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 25 }}
              className="bg-white border-t border-gray-200 max-h-[50vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                <div className="flex items-center flex-1 text-left min-w-0">
                  {" "}
                  {/* Add min-w-0 to allow flex item to shrink */}
                  <div className="flex-1 min-w-0">
                    {" "}
                    {/* Add min-w-0 to allow content to shrink */}
                    <h3 className="text-lg font-semibold text-gray-900">
                      Filtered Activities ({filteredActivities.length})
                    </h3>
                    {selectedItem ? (
                      <p className="text-sm text-primary mt-1 font-medium truncate">
                        📍 {selectedItem.title}
                      </p>
                    ) : hasMoreActivities ? (
                      <p className="text-xs text-gray-500 mt-1">
                        {activities.length} loaded • More available
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        All activities loaded
                      </p>
                    )}
                  </div>
                </div>
                {!finalError && userLocation && (
                  <button
                    onClick={handleCenterOnUser}
                    className="ml-3 bg-primary text-white p-2 rounded-full hover:bg-primary/90 transition-colors shadow-lg"
                    aria-label="Center on my location"
                  >
                    <CrosshairIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
              <div className="overflow-y-auto max-h-[40vh]">
                {filteredActivities.length > 0 ? (
                  <>
                    <div className="p-2 space-y-2">
                      {sortedFilteredActivities
                        .slice(0, visibleCount)
                        .map((activity) => (
                          <div
                            key={
                              activity.id +
                              "-" +
                              String(activity.dateTime) +
                              "-" +
                              String(activity.groupSize) +
                              "-" +
                              String(activity.participants?.length || 0)
                            }
                            data-activity-id={activity.id}
                            className={`transition-all duration-200 ${
                              selectedItem?.id === activity.id
                                ? "ring-2 ring-primary ring-offset-4 bg-primary/5 rounded-xl shadow-md"
                                : ""
                            }`}
                          >
                            <div>
                              <div className="flex items-center space-x-4">
                                {/* Left column: image and details button */}
                                <div className="flex flex-col items-center space-y-3 flex-shrink-0">
                                  <button
                                    onClick={() =>
                                      handleListItemClick(activity)
                                    }
                                    className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg"
                                    aria-label={`View details for ${activity.title}`}
                                  >
                                    <ActivityImagePreview activity={activity} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNavigateToDetails(activity);
                                    }}
                                    className="flex items-center justify-center px-2 py-1 bg-primary text-white text-xs font-semibold rounded-md hover:bg-primary/90 transition-colors w-20 mt-2"
                                    title="View details"
                                  >
                                    Details
                                  </button>
                                </div>
                                {/* Right section: ActivityListItem content (without image) */}
                                <div className="flex-1 min-w-0">
                                  <ActivityListItem
                                    activity={activity}
                                    onClick={() =>
                                      handleListItemClick(activity)
                                    }
                                    hideImage={true}
                                    showOrganizerIndicator={true}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                      {/* Show 'Load More' button if more activities are available */}
                      {visibleCount < sortedFilteredActivities.length && (
                        <div className="flex justify-center py-4">
                          <button
                            className="px-6 py-2 rounded-full bg-primary text-white font-semibold shadow hover:bg-primary/90 transition-colors"
                            onClick={() =>
                              setVisibleCount((prev) =>
                                Math.min(
                                  sortedFilteredActivities.length,
                                  prev + PAGE_SIZE,
                                ),
                              )
                            }
                          >
                            Load More Activities
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Load More Button */}
                  </>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <ListIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-semibold">No activities found</p>
                    <p className="text-sm mt-2">
                      Try adjusting your filters or changing the map area
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showPermissionDeniedPrompt && !mapError && (
            <LocationPermissionPrompt
              onAction={requestLocation}
              isRequesting={isRequestingLocation}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {finalError && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-gray-800/80 backdrop-blur-sm flex flex-col items-center justify-center text-white text-center p-4 z-50"
            >
              <XCircleIcon className="w-16 h-16 text-red-400 mb-4" />
              <h2 className="text-xl font-bold">{errorTitle}</h2>
              <p className="mt-2 text-gray-300 max-w-sm">{finalError}</p>
              {!mapError && (
                <button
                  onClick={() => requestLocation()}
                  disabled={isRequestingLocation}
                  className="mt-4 px-5 py-2 rounded-full bg-white text-gray-900 font-semibold shadow-lg hover:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isRequestingLocation ? "Retrying..." : "Retry Location"}
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <header className="absolute left-0 right-0 z-30 p-4 flex justify-between items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="bg-white backdrop-blur-md text-gray-900 p-3 rounded-full hover:bg-gray-100 transition-colors shadow-lg"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <div className="flex gap-3">
            <button
              onClick={() => setShowListView(!showListView)}
              className={`bg-white backdrop-blur-md text-gray-900 p-3 rounded-full hover:bg-gray-100 transition-colors shadow-lg ${showListView ? "ring-2 ring-primary" : ""}`}
              aria-label="Toggle list view"
            >
              <ListIcon className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  handleDeselectItem(); // Deselect any selected activity
                  setIsFilterPanelOpen(true);
                }}
                className="bg-white backdrop-blur-md text-gray-900 p-3 rounded-full hover:bg-gray-100 transition-colors shadow-lg flex items-center gap-2"
              >
                <SlidersIcon className="w-6 h-6 text-gray-700" />
                <span className="font-semibold pr-2 text-gray-900">
                  Filters
                </span>
              </button>
              {(!filters.categories.has("all") ||
                filters.time !== "any" ||
                filters.groupSize > 0) && (
                <button
                  onClick={handleResetFilters}
                  className="bg-gray-500 backdrop-blur-md text-white p-2 rounded-full hover:bg-gray-600 transition-colors shadow-lg"
                  title="Reset all filters"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </header>

        <AnimatePresence>
          {!finalError && userLocation && !showListView && (
            <motion.div
              className="absolute right-4 z-30 flex flex-col gap-3"
              style={{
                bottom: selectedItem ? "10rem" : "1.5rem", // Add 64px for bottom nav
              }}
            >
              <button
                onClick={handleCenterOnUser}
                className="bg-white/20 backdrop-blur-md text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:bg-white/30 transition-colors"
                aria-label="Center on my location"
              >
                <CrosshairIcon className="w-6 h-6" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {selectedItem && !finalError && !showListView && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 z-30 p-4"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 25,
                mass: 0.8,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-white rounded-2xl p-4 shadow-soft-lg w-full max-w-md mx-auto">
                <button
                  onClick={handleDeselectItem}
                  className="absolute top-2 right-2 bg-gray-100 text-gray-500 rounded-full p-1.5 hover:bg-gray-200 transition-colors z-10"
                  aria-label="Close details"
                >
                  <XIcon className="w-5 h-5" />
                </button>
                <div className="flex items-center space-x-4">
                  <SelectedItemImage activity={selectedItem} />
                  <div className="flex-1 overflow-hidden">
                    <h3 className="font-bold text-lg text-gray-900 truncate">
                      {selectedItem.title}
                    </h3>
                    <div className="flex items-center text-sm text-gray-500 mt-1">
                      <UsersIcon className="w-4 h-4 mr-2" />
                      <span>
                        {selectedItem.participants.length}/
                        {selectedItem.groupSize} joined
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleViewDetails(selectedItem)}
                  className="w-full mt-4 bg-primary text-white font-bold py-3 rounded-full hover:opacity-90 transition-opacity"
                >
                  View Full Details
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filter Panel */}
        <AnimatePresence>
          {isFilterPanelOpen && (
            <>
              <motion.div
                key="filter-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setIsFilterPanelOpen(false)}
                className="fixed inset-0 bg-black/50 z-40"
                style={{ willChange: "opacity" }}
              />
              <motion.div
                key="filter-panel"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="fixed bottom-0 left-0 right-0 bg-white text-gray-900 rounded-t-2xl p-4 shadow-2xl z-50 max-h-[80vh] flex flex-col"
                style={{
                  willChange: "transform",
                  paddingBottom:
                    "calc(1rem + 4rem + env(safe-area-inset-bottom))", // Extra padding for bottom nav and safe area
                }}
              >
                <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">Filters</h2>
                  <button
                    onClick={() => setIsFilterPanelOpen(false)}
                    className="p-2 rounded-full hover:bg-gray-100"
                  >
                    <XIcon className="w-6 h-6 text-gray-500" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto space-y-6 pr-2 -mr-2">
                  {/* Time Filter */}
                  <div>
                    <h3 className="font-semibold mb-2 text-gray-900">Time</h3>
                    <div className="flex space-x-2">
                      {["any", "now", "today", "tomorrow"].map((time) => (
                        <button
                          key={time}
                          onClick={() =>
                            setTempFilters({ ...tempFilters, time })
                          }
                          className={`flex-1 py-2 text-sm font-semibold rounded-full border-2 transition-colors ${tempFilters.time === time ? "bg-primary text-white border-primary" : "bg-gray-100 text-gray-700 border-transparent hover:border-gray-300"}`}
                        >
                          {time.charAt(0).toUpperCase() + time.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Category Filter */}
                  <div>
                    <h3 className="font-semibold mb-2 text-gray-900">
                      Categories
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {FILTER_CATEGORIES.map((cat) => {
                        const isSelected = tempFilters.categories.has(
                          cat.value,
                        );
                        const isAllSelected = tempFilters.categories.has("all");

                        return (
                          <button
                            key={cat.value}
                            onClick={() => {
                              const newCategories = new Set(
                                tempFilters.categories,
                              );

                              if (cat.value === "all") {
                                // Clicking "All" clears other selections
                                newCategories.clear();
                                newCategories.add("all");
                              } else {
                                // Remove "all" when selecting specific category
                                newCategories.delete("all");

                                if (isSelected) {
                                  // Deselect this category
                                  newCategories.delete(cat.value);
                                  // If nothing selected, revert to "all"
                                  if (newCategories.size === 0) {
                                    newCategories.add("all");
                                  }
                                } else {
                                  // Select this category
                                  newCategories.add(cat.value);
                                }
                              }

                              setTempFilters({
                                ...tempFilters,
                                categories: newCategories,
                              });
                            }}
                            className={`py-2 px-2 text-sm font-semibold rounded-lg border-2 flex items-center justify-center gap-2 transition-colors ${
                              isSelected ||
                              (cat.value === "all" && isAllSelected)
                                ? "bg-primary/10 text-primary border-primary"
                                : "bg-gray-100 text-gray-700 border-transparent hover:border-gray-300"
                            }`}
                          >
                            <cat.Icon
                              className={`w-4 h-4 ${isSelected || (cat.value === "all" && isAllSelected) ? "text-primary" : "text-gray-500"}`}
                            />
                            {cat.name}
                          </button>
                        );
                      })}
                    </div>
                    {!tempFilters.categories.has("all") &&
                      tempFilters.categories.size > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          {tempFilters.categories.size}{" "}
                          {tempFilters.categories.size === 1
                            ? "category"
                            : "categories"}{" "}
                          selected
                        </p>
                      )}
                  </div>

                  {/* Group Size Filter */}
                  <div>
                    <h3 className="font-semibold mb-2 text-gray-900">
                      Min. People Joined
                    </h3>
                    <div className="flex items-center justify-center space-x-4 bg-gray-100 p-2 rounded-lg">
                      <button
                        onClick={() =>
                          setTempFilters((f) => ({
                            ...f,
                            groupSize: Math.max(0, f.groupSize - 1),
                          }))
                        }
                        className="p-2 rounded-full bg-white shadow-sm"
                      >
                        <MinusIcon className="w-5 h-5 text-gray-700" />
                      </button>
                      <span className="text-lg font-bold w-16 text-center text-gray-900">
                        {tempFilters.groupSize > 0
                          ? `${tempFilters.groupSize}+`
                          : "Any"}
                      </span>
                      <button
                        onClick={() =>
                          setTempFilters((f) => ({
                            ...f,
                            groupSize: Math.min(20, f.groupSize + 1),
                          }))
                        }
                        className="p-2 rounded-full bg-white shadow-sm"
                      >
                        <PlusIcon className="w-5 h-5 text-gray-700" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
                  <button
                    onClick={handleApplyFilters}
                    className="w-full bg-primary text-white font-bold py-3.5 rounded-full shadow-lg shadow-primary/30 transform hover:scale-105 transition-transform"
                  >
                    Show {filterPanelCount} activities
                  </button>
                  <button
                    onClick={handleResetFilters}
                    className="w-full bg-gray-500 text-white font-bold py-3 rounded-full shadow-lg transform hover:scale-105 transition-transform hover:bg-gray-600"
                  >
                    Reset All Filters
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};
export default MapPage;
